import { SearchOutlined } from '@ant-design/icons';
import { OrderByTrpc } from '@evidia/shared';
import { Button, Input, Pagination, Space } from 'antd';
import type { ColumnType } from 'antd/es/table';
import type { ColumnGroupType, TableRowSelection } from 'antd/es/table/interface';
import type { Dispatch, SetStateAction } from 'react';
import React from 'react';
import { useIsTablet } from '../../hooks/useBreakpoint';
import CarLoader from '../CarLoader';
import SimpleTabs from '../SimpleTabs';
import { DefaultMobileRowItem } from './DefaultMobileRowItem';
import { MobileRowItem } from './MobileRowItem';
import { FooterActionButtons, FooterDiv, StyledTable, TableWrapper } from './styles';

export type Column = (ColumnGroupType<any> | ColumnType<any>) & ColumntBase;
export type Columns = Column[];

type DataTypeBase = {
	id: string;
};

type ColumntBase = {
	searchable?: boolean;
	dataIndex?: string | string[];
	searchKey?: string;
};

export type ColumnsWithGridKey = (Column & {
	gridKey: string;
	justifySelf?: 'start' | 'end' | 'center' | 'stretch';
	alignSelf?: 'start' | 'end' | 'center' | 'stretch';
})[];

const DEFAULT_ORDER_BY = {
	key: 'createdAt',
	order: OrderByTrpc.DESC,
};

const DEFAULT_PAGINATION = { pageSize: 10, currentPage: 1 };

interface BaseSearchType {
	pagination?: {
		pageSize: number;
		currentPage: number;
	};
}

interface BaseProps<DataType, SearchType extends BaseSearchType> {
	'data-testid'?: string;
	showHeader?: boolean;
	data: { data: DataType[]; loading: boolean };
	count: { data: number; loading: boolean };
	search?: SearchType;
	columns?: Columns;
	footer?: AnyReactElement;
	tabs?: {
		items: { label: string; key: string }[];
		onClick: (key: string) => void;
		defaultActiveKey?: string;
	};
	check?: {
		onCheck: (checkedRows: DataType[]) => void;
		checked: DataType[];
	};
	border?: boolean;
	tabletSmooth?: boolean;
	responsiveColumnsMap?: string[][];
	disableCellPadding?: boolean;
	onSearch?: Dispatch<SetStateAction<SearchType>>;
	rowClassName?: (record: DataType) => string;
	onRowClick?: (record: DataType) => void;
	renderResponsiveHeader?: () => AnyReactElement;
	renderResponsiveContent?: (record: DataType) => AnyReactElement;
}

type NoGridColumns = {
	columns?: Columns;
	responsiveColumnsMap?: never;
};

type WithGridColumns = {
	columns: ColumnsWithGridKey;
	responsiveColumnsMap: string[][];
};

type Props<DataType, SearchType extends BaseSearchType> = BaseProps<DataType, SearchType> &
	(NoGridColumns | WithGridColumns);

const SimpleTable = <DataType extends DataTypeBase, SearchType extends BaseSearchType>({
	onSearch,
	check,
	rowClassName,
	search,
	tabs,
	data,
	count,
	columns,
	footer,
	border,
	showHeader = true,
	tabletSmooth = false,
	responsiveColumnsMap,
	disableCellPadding,
	onRowClick,
	renderResponsiveHeader = () => null,
	renderResponsiveContent,
	'data-testid': dataTestId,
}: Props<DataType, SearchType>): AnyReactElement => {
	const isTablet = useIsTablet();
	const resetPagination = (): void => {
		onSearch?.(s => ({ ...s, pagination: DEFAULT_PAGINATION }));
	};

	const handleRowSelection = (localSelectedRowKeys: React.Key[], selectedRows: DataType[]): void => {
		check?.onCheck?.(selectedRows);
	};

	const getColumnSearchProps = (row: ColumntBase): ColumnType<DataType> => {
		const { dataIndex, searchKey } = row;
		const getToSearchIndex = (): string => {
			if (searchKey) {
				return searchKey;
			}
			if (dataIndex) {
				if (Array.isArray(dataIndex)) {
					return dataIndex[dataIndex.length - 1];
				}
				return dataIndex;
			}

			return 'err';
		};

		const toSearchKey = getToSearchIndex();

		return {
			filterDropdown: ({ setSelectedKeys, selectedKeys, clearFilters, close }) => (
				<div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()}>
					<Input
						placeholder="Vyhledat"
						value={selectedKeys[0]}
						onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
						onPressEnter={() => {
							onSearch?.(s => ({ ...s, [toSearchKey]: (selectedKeys[0] as string)?.trim() }));
							close();
						}}
						style={{ marginBottom: 8, display: 'block' }}
					/>
					<Space>
						<Button
							type="primary"
							onClick={() => {
								onSearch?.(s => ({ ...s, [toSearchKey]: (selectedKeys[0] as string)?.trim() }));
								close();
							}}
							icon={<SearchOutlined />}
							size="small"
							style={{ width: 150 }}
						>
							Aplikovat
						</Button>
						<Button
							onClick={() => {
								onSearch?.(s => ({ ...s, [toSearchKey]: undefined }));
								clearFilters?.();
								close();
							}}
							size="small"
							style={{ width: 90 }}
						>
							Zrušit
						</Button>
					</Space>
				</div>
			),
			filterIcon: () => (
				<SearchOutlined style={{ color: (search as any)?.[toSearchKey] ? '#1677ff' : undefined }} />
			),
		};
	};

	const renderResponsive = (record: any) => {
		if (responsiveColumnsMap) {
			return <MobileRowItem columns={columns} gridMap={responsiveColumnsMap} record={record} />;
		}

		if (renderResponsiveContent) {
			return renderResponsiveContent(record);
		}

		return <DefaultMobileRowItem columns={columns ?? []} record={record} />;
	};

	const getViewportSpecificColumns = () => {
		if (!columns) {
			return undefined;
		}

		const firstColumnData = columns?.[0];

		const responsiveColumn: Column = {
			// in further development we can add mobile responzive header with filters etc
			title: renderResponsiveHeader,
			dataIndex: firstColumnData.dataIndex,
			key: firstColumnData.key,
			render: (_text, record) => renderResponsive(record),
		};
		const columnsForOtherDevices = columns.map(c => {
			if (c.searchable) {
				return {
					...c,
					...getColumnSearchProps(c),
				};
			}
			return c;
		});

		if (isTablet) {
			return [responsiveColumn];
		}

		return columnsForOtherDevices;
	};

	let rowSelection: TableRowSelection<DataType> | undefined;
	if (check) {
		rowSelection = {
			type: 'checkbox',
			selectedRowKeys: check.checked.map(v => v.id),
			onChange: handleRowSelection,
		};
	}

	const getRowClassName = (record: any): string => {
		const cls: string[] = [];
		if (rowClassName) {
			cls.push(rowClassName(record));
		}
		if (onRowClick) {
			cls.push('clickable-row');
		}
		return cls.join(' ');
	};

	const table = (
		<>
			<StyledTable
				showHeader={isTablet ? false : showHeader}
				// ref={ref}
				onRow={onRowClick ? r => ({ onClick: () => onRowClick?.(r) }) : undefined}
				rowClassName={getRowClassName}
				rowKey={record => record.id}
				pagination={false}
				size="large"
				dataSource={data.data}
				columns={getViewportSpecificColumns()}
				loading={{
					spinning: data.loading,
					indicator: (
						<div style={{ paddingTop: '0.5em' }}>
							<CarLoader />
						</div>
					),
				}}
				onChange={(pagination, filtersOnChange, sorter: any) => {
					// celan filters
					const filters: any = Object.keys(filtersOnChange).reduce((acc, v) => {
						if (
							columns?.find((c: any): boolean => {
								if (Array.isArray(c.dataIndex)) {
									return c.dataIndex.includes(v);
								}
								return c.dataIndex === v;
							})?.filters
						) {
							return {
								...acc,
								[v]: filtersOnChange[v],
							};
						}
						return acc;
					}, {});
					//

					if (!sorter.column || !sorter.column.key) {
						onSearch?.(s => ({
							...s,
							orderBy: DEFAULT_ORDER_BY,
							filters,
						}));
						return;
					}
					const sort = sorter as { column: { key: string }; order: 'ascend' | 'descend' };

					onSearch?.(s => ({
						...s,
						orderBy: {
							key: sort.column.key,
							order: sort.order === 'ascend' ? OrderByTrpc.ASC : OrderByTrpc.DESC,
						},
						filters,
					}));
				}}
				$disablePadding={disableCellPadding}
				rowSelection={!isTablet ? rowSelection : undefined}
			/>
			<FooterDiv>
				<div>
					{!count.loading && count?.data / (search?.pagination!.pageSize ?? Number.MAX_SAFE_INTEGER) > 1 ? (
						<Pagination
							total={count?.data}
							pageSize={search?.pagination!.pageSize}
							current={search?.pagination!.currentPage}
							size="small"
							showSizeChanger={false}
							onChange={(p, ps) => {
								onSearch?.(s => ({ ...s, pagination: { pageSize: ps, currentPage: p } }));
							}}
						/>
					) : (
						<div />
					)}
				</div>
				{footer && <FooterActionButtons>{footer}</FooterActionButtons>}
			</FooterDiv>
		</>
	);

	if (!tabs) {
		return (
			<TableWrapper $border={border} $tabletSmooth={tabletSmooth} data-testid={dataTestId}>
				{table}
			</TableWrapper>
		);
	}

	return (
		<TableWrapper $border={border} $tabletSmooth={tabletSmooth} data-testid={dataTestId}>
			<SimpleTabs
				onTabClick={(k: string): void => {
					resetPagination();
					tabs.onClick(k);
				}}
				items={tabs.items.map(i => ({ key: i.key, label: i.label, children: table }))}
				defaultActiveKey={tabs.defaultActiveKey}
			/>
		</TableWrapper>
	);
};

export default SimpleTable;
